import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Spinner, Alert, Accordion, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import styled from "styled-components";
import { initiatePayment } from "../api/services/paymentService";

/* -------------------------------------------------------
 * CASHFREE MODES
 * ----------------------------------------------------- */
const CASHFREE_MODES = {
    SANDBOX: "sandbox",
    PRODUCTION: "production",
};

/* -------------------------------------------------------
 * STYLES
 * ----------------------------------------------------- */
const PaymentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 1rem;
  background-color: #f8f9fa;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 520px;
  padding: 2rem;
`;

const Header = styled.h4`
  text-align: center;
  color: #4b6cb7;
  font-weight: 600;
  margin-bottom: 1.25rem;
`;

const PriceText = styled.div`
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 1.5rem 0;
`;

const PayButton = styled(Button)`
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 8px;
`;

const InfoText = styled.p`
  font-size: 0.85rem;
  color: #666;
  text-align: center;
  margin-top: 1.5rem;
`;

const PaymentPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();

    const applicationId = state?.applicationId;
    const applicationNo = state?.applicationNo;
    const totalAmount = state?.totalAmount;

    const [cashfree, setCashfree] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedGateway, setSelectedGateway] = useState("easebuzz"); // Defaulting to easebuzz as per user's likely intent

    const cashfreeMode =
        process.env.REACT_APP_CASHFREE_ENV === "production"
            ? CASHFREE_MODES.PRODUCTION
            : CASHFREE_MODES.SANDBOX;

    /* ---------------------------------------------------
     * REDIRECT SAFETY (NO EARLY RETURN)
     * --------------------------------------------------- */
    useEffect(() => {
        if (!applicationId) {
            navigate("/", { replace: true });
        }
    }, [applicationId, navigate]);

    /* ---------------------------------------------------
     * INIT CASHFREE SDK
     * --------------------------------------------------- */
    const initCashfree = useCallback(async () => {
        try {
            const instance = await load({ mode: cashfreeMode });
            setCashfree(instance);
        } catch (err) {
            console.error("Cashfree load error:", err);
            setError("Failed to initialize payment system.");
        }
    }, [cashfreeMode]);

    useEffect(() => {
        initCashfree();
    }, [initCashfree]);

    /* ---------------------------------------------------
     * HANDLE PAYMENT
     * --------------------------------------------------- */
    const handlePayment = async () => {
        try {
            setLoading(true);
            setError("");

            // Pass selected gateway preference - backend will favor this but fallback to default if missing
            const res = await initiatePayment(applicationId, selectedGateway);

            // 1. HANDLE CASHFREE
            if (res.gateway === "cashfree") {
                if (!res?.paymentSessionId) {
                    throw new Error("Cashfree payment session not received");
                }
                if (!cashfree) {
                    throw new Error("Cashfree system not ready");
                }
                await cashfree.checkout({
                    paymentSessionId: res.paymentSessionId,
                    redirectTarget: "_self",
                });
            }

            // 2. HANDLE EASEBUZZ
            else if (res.gateway === "easebuzz") {
                if (!res?.accessKey) {
                    throw new Error("Easebuzz access key not received");
                }

                if (!window.EasebuzzCheckout) {
                    throw new Error("Easebuzz payment system not ready. Please refresh.");
                }

                if (!res.accessKey) {
                    throw new Error("Invalid access key received from server.");
                }

                console.log("Initializing Easebuzz with Key:", res.key);
                const easebuzzCheckout = new window.EasebuzzCheckout(res.key, res.env);

                const options = {
                    access_key: res.accessKey,
                    onResponse: (response) => {
                        console.log("Easebuzz response:", response);
                        navigate(`/payment-response?order_id=${res.orderId}&gateway=easebuzz`, {
                            state: { easebuzzResponse: response }
                        });
                    },
                };

                // SAFETY: Reset the spinner if nothing happens within 8 seconds
                setTimeout(() => setLoading(false), 8000);

                easebuzzCheckout.initiatePayment(options);
            }

            else {
                throw new Error("Unknown payment gateway configured");
            }

        } catch (err) {
            console.error("Payment error:", err);
            setError(err.message || "Payment initiation failed");
            setLoading(false);
        }
    };

    /* ---------------------------------------------------
     * RENDER (SAFE FALLBACK UI)
     * --------------------------------------------------- */
    if (!applicationId) {
        return (
            <PaymentContainer>
                <Spinner animation="border" />
            </PaymentContainer>
        );
    }

    return (
        <PaymentContainer>
            <StyledCard>
                <Header>Complete Your Registration</Header>

                {error && <Alert variant="danger">{error}</Alert>}

                {cashfreeMode === CASHFREE_MODES.SANDBOX && (
                    <Alert variant="warning" className="text-center">
                        Sandbox mode enabled (test payments only)
                    </Alert>
                )}

                <p><strong>Application No:</strong> {applicationNo}</p>
                <PriceText>Amount Payable: ₹{totalAmount}</PriceText>

                <div className="mb-4">
                    <label className="form-label fw-bold mb-2">Select Payment Gateway:</label>
                    <div className="d-flex gap-3">
                        <Form.Check
                            type="radio"
                            id="gateway-easebuzz"
                            label="Easebuzz"
                            name="paymentGateway"
                            value="easebuzz"
                            checked={selectedGateway === "easebuzz"}
                            onChange={(e) => setSelectedGateway(e.target.value)}
                        />
                        <Form.Check
                            type="radio"
                            id="gateway-cashfree"
                            label="Cashfree"
                            name="paymentGateway"
                            value="cashfree"
                            checked={selectedGateway === "cashfree"}
                            onChange={(e) => setSelectedGateway(e.target.value)}
                        />
                    </div>
                </div>

                <PayButton
                    variant="primary"
                    onClick={handlePayment}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Redirecting…
                        </>
                    ) : (
                        `Proceed to Pay via ${selectedGateway.charAt(0).toUpperCase() + selectedGateway.slice(1)}`
                    )}
                </PayButton>

                <Accordion className="mt-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Refund Policy</Accordion.Header>
                        <Accordion.Body>
                            Refunds are only applicable for duplicate payments.
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <InfoText>
                    By proceeding, you agree to our terms & policies.
                </InfoText>
            </StyledCard>
        </PaymentContainer>
    );
};

export default PaymentPage;
